<div class="props-page">

  <!-- En-tête ─────────────────────────────────────────────────────────────── -->
  <div class="page-top">
    <div>
      <h1 class="page-title">Biens immobiliers</h1>
      <p class="page-subtitle">Gestion et validation du patrimoine immobilier</p>
    </div>
    @if (!loadingAll() && !loadingPending()) {
      <div class="kpi-chips">
        <span class="kpi-chip kpi-total">
          <i class="pi pi-building"></i>
          {{ pageAll().totalElements }} bien{{ pageAll().totalElements > 1 ? 's' : '' }}
        </span>
        <span class="kpi-chip kpi-pending">
          <i class="pi pi-clock"></i>
          {{ pagePending().totalElements }} en attente
        </span>
      </div>
    }
  </div>

  <!-- Filtres ──────────────────────────────────────────────────────────────── -->
  <div class="filters-bar">
    <span class="search-wrap p-input-icon-left">
      <i class="pi pi-search search-icon"></i>
      <input pInputText class="search-input"
             [ngModel]="searchAll()" (ngModelChange)="onSearchChange($event)"
             placeholder="Filtrer la page affichée par titre, ville...">
      @if (searchAll()) {
        <button class="search-clear" (click)="clearSearch()"><i class="pi pi-times"></i></button>
      }
    </span>
    <p-select class="type-select" [options]="typeOptions" optionLabel="label" optionValue="value"
              [ngModel]="typeFilter()" (ngModelChange)="typeFilter.set($event)"
              placeholder="Type de bien"></p-select>
    <p-selectbutton class="view-toggle" [options]="viewOptions" optionValue="value"
                    [ngModel]="viewMode()" (ngModelChange)="onViewModeChange($event)"
                    [allowEmpty]="false" aria-label="Mode d'affichage">
      <ng-template #item let-opt>
        <i [class]="opt.icon" [pTooltip]="opt.tooltip" tooltipPosition="top"></i>
      </ng-template>
    </p-selectbutton>
  </div>
  <p class="filters-hint">
    <i class="pi pi-info-circle"></i>
    @if (viewMode() === 'kanban') {
      La recherche et le filtre par type s'appliquent aux cartes déjà chargées dans chaque colonne.
    } @else {
      La recherche et le filtre par type s'appliquent aux biens actuellement affichés sur cette page (le tri complet se fait via la pagination).
    }
  </p>

  @if (viewMode() === 'kanban') {
    <app-property-kanban [search]="searchAll()" [type]="typeFilter()" />
  } @else {

  <!-- Onglets ──────────────────────────────────────────────────────────────── -->
  <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)" class="props-tabs">
    <p-tablist>
      <p-tab value="0">
        <i class="pi pi-building tab-icon"></i>
        Tous les biens
        <span class="tab-badge">{{ pageAll().totalElements }}</span>
      </p-tab>
      <p-tab value="1">
        <i class="pi pi-hourglass tab-icon"></i>
        En attente
        @if (pagePending().totalElements > 0) {
          <span class="tab-badge tab-badge--warn">{{ pagePending().totalElements }}</span>
        }
      </p-tab>
      @if (canManageTrash) {
        <p-tab value="2">
          <i class="pi pi-trash tab-icon"></i>
          Corbeille
          @if (pageDeleted().totalElements > 0) {
            <span class="tab-badge">{{ pageDeleted().totalElements }}</span>
          }
        </p-tab>
      }
    </p-tablist>

    <p-tabpanels>

      <!-- Panel 1 : Tous les biens ─────────────────────────────────────── -->
      <p-tabpanel value="0">
        <div class="table-wrap">
          <p-table [value]="filteredAll()" [lazy]="true" (onLazyLoad)="onLazyAll($event)"
                   [paginator]="true" [rows]="pageSizeAll" [totalRecords]="pageAll().totalElements"
                   [rowsPerPageOptions]="[10,20,50]" [loading]="loadingAll()" [rowHover]="true"
                   paginatorDropdownAppendTo="body"
                   styleClass="props-table" [showCurrentPageReport]="true"
                   currentPageReportTemplate="{first} - {last} sur {totalRecords}">
            <ng-template #header>
              <tr>
                <th>Bien</th>
                <th>Type</th>
                <th>Transaction</th>
                <th>Prix</th>
                <th>Statut</th>
                <th>Date</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template #body let-p>
              <tr>
                <td class="cell-main">
                  <div class="prop-title">{{ p.title }}</div>
                  <div class="prop-location">
                    <i class="pi pi-map-marker"></i>
                    {{ p.city || '—' }}{{ p.commune ? ', ' + p.commune : '' }}
                  </div>
                </td>
                <td><span class="chip-type">{{ typeLabel(p.type) }}</span></td>
                <td>
                  <span class="chip-tx" [class.chip-tx--sale]="p.transactionType === 'VENTE'">
                    {{ p.transactionType === 'VENTE' ? 'Vente' : 'Location' }}
                  </span>
                </td>
                <td class="cell-price">{{ p.price | fcfa }}</td>
                <td><app-status-tag [label]="statusLabel(p.status)" [severity]="statusSeverity(p.status)" /></td>
                <td class="cell-date">{{ formatDate(p.createdAt) }}</td>
                <td class="cell-action">
                  <a pButton [outlined]="true" [routerLink]="['/properties', p.id]"
                     icon="pi pi-folder-open" label="Documents" class="btn-view"></a>
                </td>
              </tr>
            </ng-template>
            <ng-template #emptymessage>
              <tr>
                <td colspan="7">
                  <app-empty-state icon="pi-building" title="Aucun bien trouvé"
                                    message="Modifiez vos critères de recherche" />
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </p-tabpanel>

      <!-- Panel 2 : En attente ─────────────────────────────────────────── -->
      <p-tabpanel value="1">
        <div class="table-wrap">
          <p-table [value]="pagePending().content" [lazy]="true" (onLazyLoad)="onLazyPending($event)"
                   [paginator]="true" [rows]="pageSizePending" [totalRecords]="pagePending().totalElements"
                   [rowsPerPageOptions]="[10,20,50]" [loading]="loadingPending()" [rowHover]="true"
                   paginatorDropdownAppendTo="body"
                   styleClass="props-table" [showCurrentPageReport]="true"
                   currentPageReportTemplate="{first} - {last} sur {totalRecords}">
            <ng-template #header>
              <tr>
                <th>Bien</th>
                <th>Type</th>
                <th>Transaction</th>
                <th>Prix</th>
                <th>Soumis le</th>
                <th>Action</th>
              </tr>
            </ng-template>
            <ng-template #body let-p>
              <tr class="prop-row--urgent">
                <td class="cell-main">
                  <div class="prop-title">{{ p.title }}</div>
                  <div class="prop-location">
                    <i class="pi pi-map-marker"></i>
                    {{ p.city || '—' }}{{ p.commune ? ', ' + p.commune : '' }}
                  </div>
                </td>
                <td><span class="chip-type">{{ typeLabel(p.type) }}</span></td>
                <td>
                  <span class="chip-tx" [class.chip-tx--sale]="p.transactionType === 'VENTE'">
                    {{ p.transactionType === 'VENTE' ? 'Vente' : 'Location' }}
                  </span>
                </td>
                <td class="cell-price">{{ p.price | fcfa }}</td>
                <td class="cell-date">{{ formatDate(p.createdAt) }}</td>
                <td class="cell-action">
                  <a pButton [routerLink]="['/properties', p.id]"
                     icon="pi pi-eye" label="Examiner" class="btn-review"></a>
                </td>
              </tr>
            </ng-template>
            <ng-template #emptymessage>
              <tr>
                <td colspan="6">
                  <app-empty-state icon="pi-check-circle" iconVariant="success"
                                    title="Aucun bien en attente"
                                    message="Tous les biens ont été traités" />
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </p-tabpanel>

      <!-- Panel 3 : Corbeille ─────────────────────────────────────────── -->
      @if (canManageTrash) {
      <p-tabpanel value="2">
        <div class="table-wrap">
          <p-table [value]="pageDeleted().content" [lazy]="true" (onLazyLoad)="onLazyDeleted($event)"
                   [paginator]="true" [rows]="pageSizeDeleted" [totalRecords]="pageDeleted().totalElements"
                   [rowsPerPageOptions]="[10,20,50]" [loading]="loadingDeleted()" [rowHover]="true"
                   paginatorDropdownAppendTo="body"
                   styleClass="props-table" [showCurrentPageReport]="true"
                   currentPageReportTemplate="{first} - {last} sur {totalRecords}">
            <ng-template #header>
              <tr>
                <th>Bien</th>
                <th>Type</th>
                <th>Prix</th>
                <th>Statut avant suppression</th>
                <th>Supprimé le</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template #body let-p>
              <tr class="prop-row--deleted">
                <td class="cell-main">
                  <div class="prop-title">{{ p.title }}</div>
                  <div class="prop-location">
                    <i class="pi pi-map-marker"></i>
                    {{ p.city || '—' }}{{ p.commune ? ', ' + p.commune : '' }}
                  </div>
                </td>
                <td><span class="chip-type">{{ typeLabel(p.type) }}</span></td>
                <td class="cell-price">{{ p.price | fcfa }}</td>
                <td><app-status-tag [label]="statusLabel(p.status)" [severity]="statusSeverity(p.status)" /></td>
                <td class="cell-date">{{ formatDate(p.deletedAt) }}</td>
                <td class="cell-action">
                  <button pButton [outlined]="true" icon="pi pi-undo" label="Restaurer" class="btn-view"
                          [loading]="restoring() === p.id" [disabled]="!!restoring()" (click)="restore(p)"></button>
                </td>
              </tr>
            </ng-template>
            <ng-template #emptymessage>
              <tr>
                <td colspan="6">
                  <app-empty-state icon="pi-trash" title="Corbeille vide"
                                    message="Aucun bien supprimé" />
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </p-tabpanel>
      }

    </p-tabpanels>
  </p-tabs>
  }
</div>
